import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography, Grid, Button, Segmented, Skeleton, Alert, DatePicker } from "antd";
import { PlusOutlined, SettingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "./crmDashboardStyle.css";
import { selectAuthInfo } from "../Auth/authSelectors";
import { isAdmin } from "../../lib/functions";
import { getCrmDashboardApi } from "../../apiservice/crm";
import FloatingActions from "./components/FloatingActions";
import DashboardSummary from "./components/DashboardSummary";
import LeadsTrend from "./components/LeadsTrend";
import PipelineCard from "./components/PipelineCard";
import StatusDistribution from "./components/StatusDistribution";
import SourceBreakdown from "./components/SourceBreakdown";
import TasksSnapshot from "./components/TasksSnapshot";
import PriorityActivities from "./components/PriorityActivities";
import RecentLeads from "./components/RecentLeads";
import RevenueTrend from "./components/RevenueTrend";
import TopAgents from "./components/TopAgents";
import { PERIOD_OPTIONS } from "./crmDashboardUtils";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const { RangePicker } = DatePicker;

const RANGE_STORAGE_KEY = "crm_dashboard_range";
const CUSTOM_RANGE_STORAGE_KEY = "crm_dashboard_custom_range";
const VALID_RANGES = PERIOD_OPTIONS.map((o) => o.value);
// A year is plenty for a dashboard trend chart and keeps the per-day
// leads-trend array (and the underlying query) bounded — matches the
// server-side cap in getDashboard.
const MAX_CUSTOM_RANGE_DAYS = 366;

// Stored as "YYYY-MM-DD,YYYY-MM-DD" so a refresh while on "Custom" keeps
// showing the same dates instead of silently falling back to 30 days.
const readStoredCustomRange = () => {
  try {
    const stored = window.localStorage.getItem(CUSTOM_RANGE_STORAGE_KEY);
    const [start, end] = (stored || "").split(",");
    if (!start || !end) return null;
    const parsed = [dayjs(start), dayjs(end)];
    return parsed.every((d) => d.isValid()) ? parsed : null;
  } catch {
    return null;
  }
};

// The chosen period is a per-viewer convenience; the page works without it.
// "Custom" with no (or no longer valid) stored dates has nothing to load —
// restoring it as-is would leave the page stuck on its loading skeleton
// forever (the load effect below deliberately won't fire without dates),
// so that case falls back to 30 days instead of wedging the page.
const readStoredRange = () => {
  try {
    const stored = window.localStorage.getItem(RANGE_STORAGE_KEY);
    if (!VALID_RANGES.includes(stored)) return "30d";
    if (stored === "custom" && !readStoredCustomRange()) return "30d";
    return stored;
  } catch {
    return "30d";
  }
};

const EMPTY_DASHBOARD = {
  kpis: {},
  period: null,
  pipeline: [],
  leads_trend: [],
  lead_sources: [],
  revenue_trend: [],
  task_types: [],
  status_distribution: [],
  priority_activities: [],
  recent_leads: [],
  team: [],
};

const LoadingSkeleton = () => (
  <>
    <div className="crm-kpi-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="crm-kpi skeleton">
          <Skeleton active paragraph={{ rows: 1 }} title={{ width: "50%" }} />
        </div>
      ))}
    </div>
    <div className="crm-grid two-one">
      <div className="crm-skeleton-card"><Skeleton active paragraph={{ rows: 6 }} /></div>
      <div className="crm-skeleton-card"><Skeleton active paragraph={{ rows: 6 }} /></div>
    </div>
  </>
);

const CRMDashboard = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const authInfo = useSelector(selectAuthInfo);
  const adminUser = isAdmin(authInfo);
  const currency = authInfo?.currency || "AED";
  const country = authInfo?.country;

  const [range, setRange] = useState(readStoredRange);
  const [customRange, setCustomRange] = useState(readStoredCustomRange);
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);

  // "Custom" selected but one or both dates not chosen yet -- there's
  // nothing to load or refresh in that state.
  const customRangeIncomplete = range === "custom" && !(customRange?.[0] && customRange?.[1]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = authInfo?.userInfo?.full_name?.split(" ")[0];

  const loadDashboard = useCallback(async (selected, custom) => {
    // Only the latest request may update the page; switching periods quickly
    // must not let a slower earlier response overwrite a newer one.
    const id = ++requestId.current;
    setRefreshing(true);
    setFailed(false);
    try {
      const payload = { range: selected };
      if (selected === "custom" && custom?.[0] && custom?.[1]) {
        payload.start_date = custom[0].format("YYYY-MM-DD");
        payload.end_date = custom[1].format("YYYY-MM-DD");
      }
      const res = await getCrmDashboardApi(payload);
      if (id !== requestId.current) return;
      setDashboard({ ...EMPTY_DASHBOARD, ...(res.data || {}) });
    } catch (err) {
      if (id !== requestId.current) return;
      console.log(err);
      setFailed(true);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    // "Custom" with no (or an incomplete) date range picked yet has nothing
    // to load — wait for onCustomRangeChange to fire once both ends are set.
    if (customRangeIncomplete) return;
    loadDashboard(range, customRange);
  }, [range, customRange, customRangeIncomplete, loadDashboard]);

  const onRangeChange = (value) => {
    setRange(value);
    try {
      window.localStorage.setItem(RANGE_STORAGE_KEY, value);
    } catch {
      /* storage unavailable - the choice just isn't remembered */
    }
  };

  const onCustomRangeChange = (dates) => {
    setCustomRange(dates);
    try {
      if (dates?.[0] && dates?.[1]) {
        window.localStorage.setItem(
          CUSTOM_RANGE_STORAGE_KEY,
          `${dates[0].format("YYYY-MM-DD")},${dates[1].format("YYYY-MM-DD")}`
        );
      } else {
        window.localStorage.removeItem(CUSTOM_RANGE_STORAGE_KEY);
      }
    } catch {
      /* storage unavailable - the choice just isn't remembered */
    }
  };

  return (
    <div className="crm-dashboard">
      {/* HEADER */}
      <div className="crm-header">
        <div className="crm-header-row">
          <div>
            <Title level={isMobile ? 4 : 3} className="responsive-greeting" style={{ margin: 0, fontWeight: 700 }}>
              {greeting},{isMobile && <br />} {firstName} 👋
            </Title>
            <Text type="secondary" className="muted-dashboard-text">
              {dayjs().format("dddd, DD MMM YYYY")} · {adminUser ? "Your team's" : "Your"} sales activity &amp; follow-ups
            </Text>
          </div>
          <div className="crm-header-actions-wrap">
            <div className="crm-header-actions">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/crm/leads")}>
                Lead
              </Button>
              <Button icon={<PlusOutlined />} onClick={() => navigate("/crm/tasks")}>
                Task
              </Button>
              <Button icon={<PlusOutlined />} onClick={() => navigate("/crm/deals")}>
                Deal
              </Button>
              <Button
                className="crm-header-settings"
                icon={<SettingOutlined />}
                aria-label="CRM settings"
                onClick={() => navigate("/crm/customize")}
              />
            </div>
          </div>
        </div>

        {/* Period: drives the lead / revenue figures and the inflow chart;
            pipeline, tasks and status are always live. */}
        <div className="crm-toolbar">
          {/* Five options don't always fit a phone's width; the page has a
              global overflow-x:hidden (src/index.css) to block accidental
              sideways scroll, which would otherwise just clip "Custom" off
              the edge instead of wrapping or scrolling it into view. This
              wrapper scopes its own horizontal scroll so it stays reachable. */}
          <div className="crm-period-scroll">
            <Segmented
              className="crm-period"
              options={PERIOD_OPTIONS}
              value={range}
              onChange={onRangeChange}
              disabled={loading}
            />
          </div>
          {range === "custom" && (
            <RangePicker
              size={isMobile ? "small" : "middle"}
              value={customRange}
              onChange={onCustomRangeChange}
              disabled={loading}
              disabledDate={(d) => d.isAfter(dayjs(), "day")}
              allowClear={false}
            />
          )}
          <span className="crm-toolbar-hint">
            Period applies to new leads, lead inflow and won revenue · pipeline, tasks and status are live
          </span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={() => { if (!customRangeIncomplete) loadDashboard(range, customRange); }}
            disabled={refreshing || customRangeIncomplete}
            className="crm-refresh"
          >
            {!isMobile && "Refresh"}
          </Button>
        </div>
      </div>

      {failed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Couldn't load the dashboard"
          description="Check your connection and try again."
          action={
            <Button size="small" onClick={() => loadDashboard(range)}>
              Retry
            </Button>
          }
        />
      )}

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className={`crm-body${refreshing ? " is-refreshing" : ""}`}>
          <DashboardSummary
            data={dashboard.kpis}
            currency={currency}
            country={country}
            period={dashboard.period}
          />

          <div className="crm-grid crm-key-row">
            <StatusDistribution data={dashboard.status_distribution} />
            <PriorityActivities tasks={dashboard.priority_activities} isAdmin={adminUser} />
            <TasksSnapshot kpis={dashboard.kpis} types={dashboard.task_types} />
          </div>

          <div className="crm-grid two-one">
            <LeadsTrend data={dashboard.leads_trend} period={dashboard.period} compact />
            <PipelineCard
              pipeline={dashboard.pipeline}
              kpis={dashboard.kpis}
              currency={currency}
              country={country}
            />
          </div>

          <div className="crm-grid two">
            <SourceBreakdown data={dashboard.lead_sources} period={dashboard.period} />
            <RecentLeads leads={dashboard.recent_leads} isAdmin={adminUser} />
          </div>

          <div className={`crm-grid ${adminUser ? "two" : "one"}`}>
            <RevenueTrend data={dashboard.revenue_trend} currency={currency} country={country} />
            {adminUser && (
              <TopAgents
                data={dashboard.team}
                currency={currency}
                country={country}
                period={dashboard.period}
              />
            )}
          </div>
        </div>
      )}

      {/* FLOATING ACTION BUTTON */}
      {isMobile && <FloatingActions />}
    </div>
  );
};

export default CRMDashboard;
